import React, { useState, useEffect } from "react";
import { Grid, Card, Divider, CircularProgress, Autocomplete } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSnackbar from "components/MDSnackbar";
import { postNoResponse, post, get } from "utils/WebService";
import { useMaterialUIController } from "context";
import MigracaoDeParaDominios from "./MigracaoDeParaDominios";

export default function MigracaoLegado() {
    const [loading, setLoading] = useState(false);
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");
    const [idCliente, setIdCliente] = useState("");
    const [pkDevedor, setPkDevedor] = useState("");
    const [clienteDestinoRal, setClienteDestinoRal] = useState("");
    const [clientesRal, setClientesRal] = useState([]);
    const [deParaClientes, setDeParaClientes] = useState({});
    const [salvandoVinculo, setSalvandoVinculo] = useState(false);
    const [dataInicio, setDataInicio] = useState("");
    const [dataFim, setDataFim] = useState("");
    const [minSeqLigacao, setMinSeqLigacao] = useState("");
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    useEffect(() => {
        carregarClientesRalEDePara();
    }, []);

    const carregarClientesRalEDePara = async () => {
        try {
            const [resRal, resMappings] = await Promise.all([
                get('/migracao/clientes-ral').catch(() => []),
                get('/migracao/de-para-dominios').catch(() => ({}))
            ]);
            const ralArray = Array.isArray(resRal) ? resRal : (resRal?.list || resRal?.data || []);
            setClientesRal(ralArray);
            if (resMappings?.cliente) {
                setDeParaClientes(resMappings.cliente);
            }
        } catch (e) {
            console.error("Erro ao carregar clientes RAL ou de-para:", e);
        }
    };

    useEffect(() => {
        const idTrim = idCliente.trim();
        if (idTrim && deParaClientes[idTrim]) {
            setClienteDestinoRal(String(deParaClientes[idTrim]));
        } else {
            setClienteDestinoRal("");
        }
    }, [idCliente, deParaClientes]);

    const showSnackbar = (msg, isSuccess) => {
        setSbMessage(msg);
        if (isSuccess) setSuccessSB(true);
        else setErrorSB(true);
    };

    const handleSalvarVinculoCliente = async () => {
        if (!idCliente.trim()) {
            showSnackbar("Informe o ID do Cliente no Legado primeiro!", false);
            return;
        }
        setSalvandoVinculo(true);
        try {
            await post('/migracao/salvar-de-para-cliente', {
                idClienteLegado: idCliente.trim(),
                idPessoaRal: clienteDestinoRal || ""
            });
            setDeParaClientes(prev => ({
                ...prev,
                [idCliente.trim()]: clienteDestinoRal || ""
            }));
            if (clienteDestinoRal) {
                const cEncontrado = clientesRal.find(c => String(c.id) === String(clienteDestinoRal));
                const cNome = cEncontrado?.nome || clienteDestinoRal;
                showSnackbar(`Sucesso: Fichas do Cliente Legado ${idCliente} serão associadas ao Cliente RAL ${cNome}!`, true);
            } else {
                showSnackbar(`Vínculo do Cliente Legado ${idCliente} removido. Ao migrar, um novo cliente será criado se não existir por documento.`, true);
            }
        } catch (e) {
            showSnackbar(`Erro ao salvar vínculo de cliente: ${e.message || 'Erro desconhecido'}`, false);
        } finally {
            setSalvandoVinculo(false);
        }
    };

    const runMigration = async (endpoint, name, extraPayload = {}) => {
        if (!window.confirm(`Deseja iniciar a migração de ${name}?`)) return;
        setLoading(true);
        try {
            const payload = {
                ...(idCliente.trim() ? { idClienteLegado: idCliente.trim() } : {}),
                ...(pkDevedor.trim() ? { pkDevedor: pkDevedor.trim() } : {}),
                ...extraPayload
            };
            await postNoResponse(endpoint, payload);
            showSnackbar(`Migração de ${name} iniciada em segundo plano! Acompanhe o progresso no terminal do backend.`, true);
        } catch (e) {
            showSnackbar(`Erro ao iniciar ${name}: ${e.message || 'Erro desconhecido'}`, false);
        } finally {
            setLoading(false);
        }
    };

    const handleLimparCliente = async () => {
        if (!idCliente.trim()) {
            showSnackbar("Informe o ID do Cliente no Legado para efetuar a limpeza!", false);
            return;
        }
        if (!window.confirm(`ATENÇÃO: Deseja apagar TODOS os dados migrados (fichas, contratos, acordos, históricos) e o De-Para do Cliente ID ${idCliente}? Essa ação não pode ser desfeita.`)) {
            return;
        }
        setLoading(true);
        try {
            const res = await post('/migracao/limpar-cliente', { idClienteLegado: idCliente.trim() });
            showSnackbar(res?.mensagem || `Dados do Cliente ${idCliente} limpos com sucesso!`, true);
            carregarClientesRalEDePara();
        } catch (e) {
            showSnackbar(`Erro ao limpar dados do cliente: ${e.message || 'Erro desconhecido'}`, false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Grid container display="flex" justifyContent="center">
            <Grid item xs={12} md={12}>
                <Card variant="outlined" sx={{ 
                    p: 3, 
                    mb: 2, 
                    borderColor: "divider" 
                }}>
                    <MDBox>
                        <MDTypography variant="h4" fontWeight="bold" color={darkMode ? "white" : "dark"}>Migração de Dados (Legado)</MDTypography>
                        <MDTypography variant="button" color="text" mb={2}>
                            Execute os passos abaixo sequencialmente para migrar os dados do banco antigo para o novo.
                        </MDTypography>
                        
                        <MDBox mt={3} mb={3} p={3} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                            <MDTypography variant="h6" fontWeight="medium" color={darkMode ? "white" : "dark"}>Filtros e Associação de Cliente (De-Para)</MDTypography>
                            <MDTypography variant="caption" color="text" display="block" mb={2}>
                                Se preenchido, apenas os dados pertencentes a este Cliente ou PK/Código do Devedor Legado serão migrados. Você também pode associar este cliente legado a outro cliente já existente no RAL.
                            </MDTypography>
                            
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={3}>
                                    <MDInput 
                                        type="number" 
                                        label="ID do Cliente no Legado" 
                                        fullWidth
                                        value={idCliente} 
                                        onChange={(e) => setIdCliente(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <MDInput 
                                        type="text" 
                                        label="PK / Código do Devedor (Legado)" 
                                        fullWidth
                                        value={pkDevedor} 
                                        onChange={(e) => setPkDevedor(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={5}>
                                    <MDButton 
                                        variant="gradient" 
                                        color="error" 
                                        disabled={loading || !idCliente.trim()} 
                                        onClick={handleLimparCliente}
                                        fullWidth
                                    >
                                        Limpar Dados Migrados do Cliente
                                    </MDButton>
                                </Grid>
                            </Grid>

                            <Grid container spacing={2} alignItems="center" mt={0.5}>
                                <Grid item xs={12} md={8}>
                                    <Autocomplete
                                        options={clientesRal}
                                        getOptionLabel={(option) => `${option.id} - ${option.nome || ''} ${option.documento ? '(' + option.documento + ')' : ''}`}
                                        value={clientesRal.find(c => String(c.id) === String(clienteDestinoRal)) || null}
                                        onChange={(e, newVal) => setClienteDestinoRal(newVal ? String(newVal.id) : "")}
                                        renderInput={(params) => (
                                            <MDInput 
                                                {...params} 
                                                label="Cliente Destino no RAL (De-Para / Redirecionar Carteira)" 
                                                placeholder="Selecione o cliente no RAL para onde os devedores devem migrar..." 
                                            />
                                        )}
                                        fullWidth
                                    />
                                    {idCliente.trim() && deParaClientes[idCliente.trim()] ? (
                                        <MDTypography variant="caption" color="success" fontWeight="medium" display="block" mt={0.5}>
                                            ✓ Mapeado no De-Para para o Cliente ID {deParaClientes[idCliente.trim()]} ({clientesRal.find(c => String(c.id) === String(deParaClientes[idCliente.trim()]))?.nome || 'Cliente RAL'})
                                        </MDTypography>
                                    ) : (
                                        <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                                            Opcional. Permite redirecionar todos os devedores deste cliente legado para outro cliente já cadastrado no RAL.
                                        </MDTypography>
                                    )}
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <MDButton 
                                        variant="gradient" 
                                        color="info" 
                                        disabled={loading || salvandoVinculo || !idCliente.trim()} 
                                        onClick={handleSalvarVinculoCliente}
                                        fullWidth
                                    >
                                        {salvandoVinculo ? <CircularProgress size={20} color="white" /> : "Salvar De-Para de Cliente"}
                                    </MDButton>
                                </Grid>
                            </Grid>
                        </MDBox>

                        <Divider sx={{ my: 3 }} />
                        
                        {loading && (
                            <MDBox display="flex" flexDirection="column" alignItems="center" my={3}>
                                <CircularProgress color="info" />
                                <MDTypography variant="caption" mt={1}>Processando migração. Isso pode demorar...</MDTypography>
                            </MDBox>
                        )}

                        <MDBox display="flex" flexDirection="column" gap={2} mt={2}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MDBox>
                                    <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>0. Migrar Usuários (Cobradores)</MDTypography>
                                    <MDTypography variant="caption" color="text">Importa os usuários/operadores do legado.</MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" disabled={loading} onClick={() => runMigration('/migracao/usuarios', 'Usuários (Cobradores)')}>Executar</MDButton>
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MDBox>
                                    <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>0.5. Migrar Tipos de Ocorrência</MDTypography>
                                    <MDTypography variant="caption" color="text">Importa o cadastro de tipos de ocorrências.</MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" disabled={loading} onClick={() => runMigration('/migracao/tipos-ocorrencia', 'Tipos de Ocorrência')}>Executar</MDButton>
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MDBox>
                                    <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>0.6. Migrar Tipos de Ação (Judicial)</MDTypography>
                                    <MDTypography variant="caption" color="text">Importa os tipos de ações da tabela do legado para a tabela de ações (tbAcao).</MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" disabled={loading} onClick={() => runMigration('/migracao/tipos-acao', 'Tipos de Ação')}>Executar</MDButton>
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MDBox>
                                    <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>1. Migrar Clientes (Credores)</MDTypography>
                                    <MDTypography variant="caption" color="text">Importa os dados básicos dos credores (TbPessoa).</MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" disabled={loading} onClick={() => runMigration('/migracao/clientes', 'Clientes')}>Executar</MDButton>
                            </MDBox>

                            <MDBox p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MigracaoDeParaDominios showSnackbar={showSnackbar} idCliente={idCliente} />
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MDBox>
                                    <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>2. Migrar Fichas e Devedores</MDTypography>
                                    <MDTypography variant="caption" color="text">Cria as Fichas (TbDevedorCarteira) e os Devedores (TbDevedor).</MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" disabled={loading} onClick={() => runMigration('/migracao/fichas', 'Fichas')}>Executar</MDButton>
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MDBox>
                                    <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>3. Migrar Contratos e Dívidas</MDTypography>
                                    <MDTypography variant="caption" color="text">Importa os débitos associando-os às fichas geradas no passo anterior.</MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" disabled={loading} onClick={() => runMigration('/migracao/contratos', 'Contratos/Dívidas')}>Executar</MDButton>
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MDBox>
                                    <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>4. Migrar Acordos e Lançamentos</MDTypography>
                                    <MDTypography variant="caption" color="text">Importa acordos firmados e pagamentos (lançamentos).</MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" disabled={loading} onClick={() => runMigration('/migracao/acordos', 'Acordos e Lançamentos')}>Executar</MDButton>
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MDBox>
                                    <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>5. Migrar Boletos Físicos</MDTypography>
                                    <MDTypography variant="caption" color="text">Importa os boletos emitidos e sincroniza a sequência do sistema.</MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" disabled={loading} onClick={() => runMigration('/migracao/boletos', 'Boletos')}>Executar</MDButton>
                            </MDBox>

                            <MDBox p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                    <MDBox>
                                        <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>6. Migrar Histórico de Ligações</MDTypography>
                                        <MDTypography variant="caption" color="text">Importa o histórico de cobrança e gravações telefônicas.</MDTypography>
                                    </MDBox>
                                    <MDButton variant="gradient" color="info" disabled={loading} onClick={() => runMigration('/migracao/ligacoes', 'Histórico de Ligações', { dataInicio, dataFim, minSeqLigacao })}>Executar</MDButton>
                                </MDBox>
                                <Grid container spacing={2} mt={1}>
                                    <Grid item xs={12} md={4}>
                                        <MDInput type="date" label="Data Início" fullWidth value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} InputLabelProps={{ shrink: true }} />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <MDInput type="date" label="Data Fim" fullWidth value={dataFim} onChange={(e) => setDataFim(e.target.value)} InputLabelProps={{ shrink: true }} />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <MDInput type="number" label="A partir do SeqLigação" fullWidth value={minSeqLigacao} onChange={(e) => setMinSeqLigacao(e.target.value)} />
                                    </Grid>
                                </Grid>
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MDBox>
                                    <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>7. Migrar Processos Ajuizados (BVJuridico)</MDTypography>
                                    <MDTypography variant="caption" color="text">Importa os processos ajuizados do BVJuridico amarrando diretamente à ficha do devedor.</MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" disabled={loading} onClick={() => runMigration('/migracao/processos', 'Processos Ajuizados (BVJuridico)')}>Executar</MDButton>
                            </MDBox>
                        </MDBox>
                    </MDBox>
                </Card>
            </Grid>
            
            <MDSnackbar
                color="success"
                icon="check"
                title="Sucesso"
                content={sbMessage}
                open={successSB}
                onClose={() => setSuccessSB(false)}
                close={() => setSuccessSB(false)}
                bgWhite={!darkMode}
            />
            <MDSnackbar
                color="error"
                icon="warning"
                title="Erro"
                content={sbMessage}
                open={errorSB}
                onClose={() => setErrorSB(false)}
                close={() => setErrorSB(false)}
                bgWhite={!darkMode}
            />
        </Grid>
    );
}
